import { useEffect } from "react";
import { useForm } from "react-hook-form";

import { yupResolver } from "@hookform/resolvers/yup";
import { useQueryClient } from "@tanstack/react-query";
import { object, string } from "yup";

import { Grid, Typography } from "@mui/material";

import { useAsyncInvalidateQuery } from "~/api/_shared/hooks";
import {
  HealthSystem,
  useArchiveHealthSystemMutation,
  useCreateHealthSystemMutation,
  useRestoreHealthSystemMutation,
  useUpdateHealthSystemMutation,
} from "~/api/healthSystem";

import { EHierarchyModelViewType, HierarchyModelBase } from "#/features/Hierarchy/types";
import { CustomInputControlled } from "@/common/components/Input";
import CustomModal from "@/common/components/Modal";
import { useCurrentRole } from "@/common/hooks";
import { useErrors } from "@/common/hooks/useErrors";
import { useToast } from "@/common/hooks/useToast";
import { getMaxMsg, requiredMsg } from "@/common/validation/messages";

import { ArchiveAtForm } from "./ArchiveAtForm";

const HealthSystemFormModal = ({
  selected,
  onComplete,
}: {
  selected?: HierarchyModelBase;
  onComplete: (updated?: boolean) => void;
}) => {
  const isAdmin = useCurrentRole().userIsAdmin;
  const asyncInvalidateQuery = useAsyncInvalidateQuery();
  const { showSuccess } = useToast();
  const { handleErrors } = useErrors();
  const queryClient = useQueryClient();

  const id = selected?.id;

  const formSchema = {
    name: string().max(50, getMaxMsg(50)).required(requiredMsg),
  };

  const {
    control,
    reset,
    formState: { errors, isValid, isDirty },
    getValues,
    setError,
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(object().shape(formSchema).required()),
  });

  useEffect(() => {
    if (selected) {
      reset({
        name: selected?.name,
      });
    } else {
      reset({
        name: "",
      });
    }
  }, [selected, reset]);

  const onError = (error: unknown) => handleErrors(error, setError);

  const onSettled = () => {
    void queryClient.invalidateQueries({ queryKey: ["appConfig"] });
  };

  const { mutateAsync: archiveMutation } = useArchiveHealthSystemMutation({});
  const { mutateAsync: restoreMutation } = useRestoreHealthSystemMutation({});

  const { mutate: createMutation, isPending: createApiLoading } = useCreateHealthSystemMutation({
    onSuccess: () => {
      reset();
      showSuccess("New health system created successfully");
      onComplete(true);
    },
    onError,
    onSettled,
  });

  const { mutate: updateMutation, isPending: editApiLoading } = useUpdateHealthSystemMutation({
    onSuccess: () => {
      if (id) {
        reset();
        showSuccess("Health system updated successfully");
        onComplete(true);
      }
    },
    onError,
    onSettled,
  });

  const handleUpdate = () => {
    const { name } = getValues() as {
      name: string;
    };

    const body = {
      id: selected?.id,
      name,
    };

    if (selected?.id) {
      updateMutation(body as HealthSystem.API.Update.QueryParams);
    } else {
      createMutation(body);
    }
  };

  const onSecondaryBtnClick = () => {
    reset();
    onComplete();
  };

  const isLoading = editApiLoading || createApiLoading;

  const modalContent = (
    <Grid container flexDirection="column" data-testid={"health-system-modal-content"}>
      <Grid item mb={2}>
        <Typography fontSize="0.875rem" sx={{ opacity: 0.6 }}>
          * Required information
        </Typography>
      </Grid>
      <Grid item>
        <CustomInputControlled
          label="Health system name*"
          name="name"
          control={control}
          errors={errors}
          fullWidth
        />
      </Grid>
      {isAdmin && selected?.id && (
        <ArchiveAtForm
          type={EHierarchyModelViewType.healthSystem}
          selected={selected}
          sx={{ position: "absolute", bottom: 32, right: 32 }}
          handleDelete={(_event, setOpen) => {
            void (async () => {
              try {
                await archiveMutation({ id: selected.id ?? "" });
                showSuccess("Health system archived successfully");
                setOpen(false);
                await asyncInvalidateQuery(null);
                onComplete(true);
              } catch (error) {
                onError(error);
              }
            })();
          }}
          handleRestore={(_event, setOpen) => {
            void (async () => {
              try {
                await restoreMutation({ id: selected.id ?? "" });
                showSuccess("Health system restored successfully");
                setOpen(false);
                await asyncInvalidateQuery(null);
                onComplete(true);
              } catch (error) {
                onError(error);
              }
            })();
          }}
        />
      )}
    </Grid>
  );

  return (
    <CustomModal
      isOpen={!!selected}
      modalContent={modalContent}
      modalHeaderText={selected?.id ? "Edit Health System" : "Create a new health system"}
      primaryBtnText={selected?.id ? "Save changes" : "Create"}
      onSecondaryBtnClick={onSecondaryBtnClick}
      onSubmit={handleUpdate}
      primaryDisabled={!isValid || isLoading || !isDirty}
    />
  );
};

export default HealthSystemFormModal;
